import React, { useState, useEffect, useRef } from 'react';
import { supabase, registerAuthSession, getApprovedReviews } from './supabaseClient';
import { LogoIcon } from './Logo';
import { useLanguage } from './LanguageContext';

// ─── مزايا المنصة — تُعرض عند عدم وجود آراء حقيقية معتمدة ───
const HIGHLIGHTS = [
  { icon: '📚', title: '15,000+ سؤال', desc: 'بنك أسئلة شامل ومصنّف حسب المادة والمستوى' },
  { icon: '⏱️', title: 'محاكاة الاختبار', desc: 'مؤقّت وأسلوب أسئلة مطابق لأجواء الاختبار الحقيقي' },
  { icon: '🎯', title: 'قدرات · تحصيلي · STEP', desc: 'تغطية للاختبارات الثلاثة في منصة واحدة' },
  { icon: '📊', title: 'تقارير أداء', desc: 'تتبّع مستواك في كل قسم وراجع أخطاءك' },
];

function Highlights() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {HIGHLIGHTS.map(h => (
        <div key={h.title} style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          background: 'linear-gradient(135deg, rgba(124,109,248,0.10), rgba(168,85,247,0.06))',
          border: '1px solid rgba(124,109,248,0.22)',
          borderRadius: 14, padding: '14px 16px',
        }}>
          <span style={{ fontSize: 22, lineHeight: 1.2, flexShrink: 0 }}>{h.icon}</span>
          <div>
            <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14, marginBottom: 3 }}>
              {h.title}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12.5, lineHeight: 1.7 }}>
              {h.desc}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Testimonials() {
  const { t } = useLanguage();
  const [idx, setIdx] = useState(0);
  const [list, setList] = useState([]);      // آراء حقيقية معتمدة فقط
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getApprovedReviews(12).then(({ data }) => {
      if (data?.length) {
        const real = data.map(r => ({
          name:   r.reviewer_name || r.display_name || 'مستخدم',
          role:   r.stage         || 'مستخدم قادر',
          avatar: '⭐',
          stars:  r.stars,
          text:   r.comment || '',
        })).filter(r => r.text);
        if (real.length) setList(real);
      }
      setLoaded(true);
    });
  }, []);

  useEffect(() => {
    if (list.length < 2) return;
    const timer = setInterval(() => setIdx(i => (i + 1) % list.length), 4500);
    return () => clearInterval(timer);
  }, [list]);

  // ما زال التحميل جارياً — لا نعرض شيئاً لتفادي الوميض
  if (!loaded) return null;

  const hasReviews  = list.length > 0;
  const testimonial = hasReviews ? list[Math.min(idx, list.length - 1)] : null;

  return (
    <div style={{ width: '100%', maxWidth: 380, marginTop: 32 }}>

      {/* ─── عنوان القسم ─── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, justifyContent: 'center' }}>
        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
        <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {hasReviews ? t('auth_testimonialTitle') : 'لماذا قادر؟'}
        </span>
        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
      </div>

      {/* ─── لا توجد آراء معتمدة بعد: نعرض مزايا المنصة ─── */}
      {!hasReviews && <Highlights />}

      {/* ─── آراء حقيقية معتمدة ─── */}
      {hasReviews && (
        <>
          <div style={{
            background: 'linear-gradient(135deg, rgba(124,109,248,0.12), rgba(168,85,247,0.07))',
            border: '1px solid rgba(124,109,248,0.25)',
            borderRadius: 20,
            padding: '20px 22px',
            position: 'relative',
            minHeight: 120,
            transition: 'all 0.4s',
            boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
          }}>
            {/* اقتباس زخرفي */}
            <span style={{
              position: 'absolute', top: 12, right: 18,
              fontSize: 40, color: 'rgba(124,109,248,0.2)',
              fontFamily: 'Georgia, serif', lineHeight: 1, userSelect: 'none',
            }}>"</span>

            {/* النجوم */}
            <div style={{ color: '#f59e0b', fontSize: 13, marginBottom: 10, letterSpacing: 2 }}>
              {'★'.repeat(testimonial.stars)}{'☆'.repeat(5 - testimonial.stars)}
            </div>

            {/* النص */}
            <p style={{
              color: 'rgba(255,255,255,0.8)', fontSize: 13.5,
              margin: '0 0 16px', lineHeight: 1.75,
              fontStyle: 'italic',
            }}>
              {testimonial.text}
            </p>

            {/* صاحب التعليق */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'rgba(124,109,248,0.25)',
                border: '1.5px solid rgba(124,109,248,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, flexShrink: 0,
              }}>
                {testimonial.avatar}
              </div>
              <div>
                <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 13 }}>{testimonial.name}</div>
                <div style={{
                  color: 'var(--text-muted)', fontSize: 11,
                  display: 'flex', alignItems: 'center', gap: 5, marginTop: 2,
                }}>
                  <span style={{
                    background: 'rgba(124,109,248,0.18)',
                    border: '1px solid rgba(124,109,248,0.3)',
                    borderRadius: 20, padding: '1px 8px',
                    color: '#a78bfa', fontSize: 10, fontWeight: 600,
                  }}>{testimonial.role}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ─── النقاط ─── */}
          {list.length > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginTop: 14 }}>
              {list.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} style={{
                  width: i === idx ? 22 : 6, height: 6,
                  borderRadius: 4, border: 'none', padding: 0,
                  background: i === idx ? '#7c6df8' : 'rgba(255,255,255,0.18)',
                  cursor: 'pointer', transition: 'all 0.35s',
                }} />
              ))}
            </div>
          )}

          {/* ─── إجمالي التقييمات ─── */}
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            gap: 6, marginTop: 10,
            color: 'var(--text-muted)', fontSize: 11,
          }}>
            <span style={{ color: '#f59e0b' }}>★</span>
            <span>{list.length} {list.length === 1 ? 'رأي' : 'آراء'} من مستخدمي قادر</span>
          </div>
        </>
      )}
    </div>
  );
}


// ─── حد إعادة طلب الكود — يُحفظ لكل رقم ويبقى بعد تحديث الصفحة ───
const OTP_COOLDOWNS = [60, 120, 300]; // الانتظار بعد الإرسال الأول/الثاني/الثالث
const OTP_MAX_PER_HOUR = 3;
const HOUR = 3600000;
const otpKey = (p) => `qadder_otp_${p}`;

const getOtpHistory = (p) => {
  try {
    const since = Date.now() - HOUR;
    return (JSON.parse(localStorage.getItem(otpKey(p))) || []).filter(t => t > since);
  } catch { return []; }
};

const recordOtpSend = (p) => {
  try { localStorage.setItem(otpKey(p), JSON.stringify([...getOtpHistory(p), Date.now()])); } catch {}
};

// كم ثانية باقية قبل السماح بإرسال جديد (0 = مسموح)
const otpWaitSeconds = (p) => {
  const h = getOtpHistory(p);
  if (!h.length) return 0;
  if (h.length >= OTP_MAX_PER_HOUR) return Math.max(0, Math.ceil((h[0] + HOUR - Date.now()) / 1000));
  const cd = OTP_COOLDOWNS[Math.min(h.length - 1, OTP_COOLDOWNS.length - 1)];
  return Math.max(0, Math.ceil((h[h.length - 1] + cd * 1000 - Date.now()) / 1000));
};

const fmtWait = (s) => s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : `${s}s`;

// رسالة Supabase عند تجاوز الحد من جهة الخادم (429)
const serverWait = (error) => {
  const m = error?.message?.match(/after (\d+) seconds/);
  return error?.status === 429 || m ? (m ? Number(m[1]) : 60) : 0;
};

export default function PhoneAuth({ onSuccess, onGuest, theme, setTheme }) {
  const { t, isRTL } = useLanguage();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const phoneRef = useRef(null);
  const otpRef   = useRef(null);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer(p => p - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  // auto-focus: جوال عند الفتح، OTP عند الانتقال إليها
  useEffect(() => {
    setTimeout(() => {
      if (step === 'phone') phoneRef.current?.focus();
      else                  otpRef.current?.focus();
    }, 100);
  }, [step]);

  // توحيد صيغة الجوال — يعالج 05xx / 5xx / 966xx / +966xx / +20xx
  const formatPhone = (raw) => {
    const cleaned = (raw || '').replace(/[\s-]/g, '');
    if (cleaned.startsWith('+')) return cleaned.slice(1);   // رقم دولي مكتوب بـ +
    if (cleaned.startsWith('966')) return cleaned;          // 966 مكتوبة أصلاً — لا نكررها
    if (cleaned.startsWith('00')) return cleaned.slice(2);  // صيغة 00 الدولية
    if (cleaned.startsWith('0')) return '966' + cleaned.slice(1);
    return '966' + cleaned;
  };

  const isAdminPhone = (raw) => {
    const adminPhones = (process.env.REACT_APP_ADMIN_PHONES || '').split(',').map(p => p.trim());
    const formatted = formatPhone(raw);
    return adminPhones.some(p => formatPhone(p) === formatted);
  };

  const requestOTP = async () => {
    const formatted = formatPhone(phone);
    const wait = otpWaitSeconds(formatted);
    if (wait > 0) {
      setStep('otp');
      setResendTimer(wait);
      setError(`تنبيه: أرسلنا لك كود قبل قليل — تقدر تطلب كود جديد بعد ${fmtWait(wait)}`);
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithOtp({ phone: formatted });
    setLoading(false);

    if (!error) {
      recordOtpSend(formatted);
      setStep('otp');
      setResendTimer(otpWaitSeconds(formatted));
      return;
    }
    const sw = serverWait(error);
    if (sw) {
      setStep('otp');
      setResendTimer(sw);
      setError(`تنبيه: طلبات كثيرة — حاول بعد ${fmtWait(sw)}`);
      return;
    }
    const isTwilioBlock = /blocked|Twilio|60410/.test(error.message || '');
    if (isTwilioBlock && isAdminPhone(phone)) {
      setStep('otp');
      setResendTimer(60);
      setError('تنبيه: فشل إرسال SMS من Twilio — ابحث عن الكود في Supabase Dashboard → Authentication → Logs');
    } else {
      setError(error.message || 'حدث خطأ، تأكد من الرقم وحاول مجدداً');
    }
  };

  const sendOTP = async () => {
    setError('');
    if (!phone || phone.length < 9) { setError(t('auth_invalidPhone')); return; }
    await requestOTP();
  };

  const resendOTP = async () => {
    if (resendTimer > 0) return;
    setError(''); setOtp('');
    await requestOTP();
  };

  const verifyOTP = async () => {
    setError('');
    if (!otp || otp.length < 4) { setError(t('auth_invalidCode')); return; }
    setLoading(true);
    const formatted = formatPhone(phone);

    const { data, error } = await supabase.auth.verifyOtp({ phone: formatted, token: otp, type: 'sms' });
    setLoading(false);
    if (error) setError(error.message || 'رمز التحقق غير صحيح أو منتهي الصلاحية');
    else if (typeof onSuccess === 'function') {
      if (data.user?.id) {
        // ملاحظة: إنشاء صف المستخدم في public.users وترحيل اشتراكات الأدمن
        // يتمّان الآن عبر trigger في قاعدة البيانات (on_auth_user_signed_in)
        // فلا نعتمد على المتصفح في ذلك.
        try {
          await registerAuthSession(data.user.id);
        } catch (e) {
          console.error('[registerAuthSession]', e);
        }

        // تحديث last_login — إن فشل لا يمنع الدخول
        const { error: loginErr } = await supabase
          .from('users')
          .update({ last_login: new Date().toISOString() })
          .eq('id', data.user.id);
        if (loginErr) console.error('[last_login]', loginErr.message);
      }
      onSuccess(data.user);
    }
  };

  const cardStyle = {
    width: '100%', maxWidth: '380px',
    background: 'var(--bg-card)',
    borderRadius: '20px',
    border: '1px solid var(--border)',
    padding: '32px 24px',
  };

  const inputStyle = {
    width: '100%', padding: '14px 16px', borderRadius: '12px',
    background: 'var(--bg-input)',
    border: '1.5px solid var(--border)',
    color: 'var(--text)', textAlign: 'center', outline: 'none',
    boxSizing: 'border-box', fontFamily: 'inherit',
  };

  const primaryBtn = {
    width: '100%', marginTop: '16px', padding: '15px',
    borderRadius: '12px',
    background: loading ? 'rgba(124,58,237,0.5)' : 'linear-gradient(135deg, #7c3aed, #a855f7)',
    border: 'none', color: '#fff', fontSize: '16px', fontWeight: '700',
    fontFamily: 'inherit', cursor: loading ? 'not-allowed' : 'pointer',
    boxShadow: '0 8px 24px rgba(124,58,237,0.4)',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-page)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '24px', direction: isRTL ? 'rtl' : 'ltr',
      fontFamily: "'Tajawal', sans-serif",
      position: 'relative',
    }}>


      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <div style={{ margin: '0 auto 16px', width: 'fit-content' }}>
          <LogoIcon size={90} />
        </div>
        <h1 style={{ color: '#f0b429', fontSize: '32px', fontWeight: '800', margin: '0 0 4px' }}>
          {t('appName')}
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0 }}>
          {t('auth_tagline')}
        </p>
      </div>

      <div style={cardStyle}>
        {step === 'phone' ? (
          <>
            <h2 style={{ color: '#fff', fontSize: '20px', fontWeight: '700', margin: '0 0 8px' }}>
              {t('auth_enterPhone')}
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '0 0 24px' }}>
              {t('auth_smsSent')}
            </p>
            <input
              ref={phoneRef}
              type="text" inputMode="numeric" pattern="[0-9]*"
              value={phone}
              onChange={e => setPhone(e.target.value.replace(/[^0-9+]/g, '').slice(0, 16))}
              onKeyDown={e => e.key === 'Enter' && !loading && sendOTP()}
              placeholder="05XXXXXXXX"
              style={{ ...inputStyle, fontSize: '18px', letterSpacing: '2px' }}
            />
            {error && (
              <p style={{ color: '#ff6b6b', fontSize: '13px', margin: '8px 0 0', textAlign: 'center' }}>
                {error}
              </p>
            )}
            <button onClick={sendOTP} disabled={loading} style={primaryBtn}>
              {loading ? t('auth_sending') : t('auth_sendCode')}
            </button>

            {/* زر الضيف */}
            {onGuest && (
              <button onClick={onGuest} style={{
                marginTop: 12, width: '100%', padding: '12px',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: '12px', cursor: 'pointer',
                color: 'var(--text-secondary)', fontSize: '14px',
                fontFamily: "'Tajawal', sans-serif", fontWeight: 600,
                transition: 'all 0.2s',
              }}>
                {t('auth_browseAsGuest')}
              </button>
            )}
          </>
        ) : (
          <>
            <h2 style={{ color: '#fff', fontSize: '20px', fontWeight: '700', margin: '0 0 8px' }}>
              {t('auth_verifyTitle')}
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '0 0 24px' }}>
              {t('auth_codeSentTo')} {phone}
            </p>
            <input
              ref={otpRef}
              type="text" inputMode="numeric" pattern="[0-9]*"
              value={otp}
              onChange={e => {
                const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                setOtp(val);
              }}
              onKeyDown={e => e.key === 'Enter' && !loading && verifyOTP()}
              placeholder="------"
              style={{ ...inputStyle, fontSize: '24px', letterSpacing: '8px' }}
            />
            {error && (
              <p style={{
                color: error.startsWith('تنبيه') ? '#f59e0b' : '#ff6b6b',
                fontSize: '13px', margin: '8px 0 0', textAlign: 'center', lineHeight: 1.6,
              }}>
                {error}
              </p>
            )}
            <button onClick={verifyOTP} disabled={loading} style={primaryBtn}>
              {loading ? t('auth_verifying') : t('auth_verify')}
            </button>
            <button
              onClick={resendOTP}
              disabled={resendTimer > 0 || loading}
              style={{
                width: '100%', marginTop: '12px', padding: '12px',
                borderRadius: '12px', background: 'none',
                border: '1px solid var(--border)',
                color: resendTimer > 0 ? 'var(--text-muted)' : 'rgba(255,255,255,0.6)',
                fontSize: '14px', fontFamily: 'inherit',
                cursor: resendTimer > 0 ? 'not-allowed' : 'pointer',
              }}
            >
              {resendTimer > 0 ? `${t('auth_resendAfter')} ${fmtWait(resendTimer)}` : t('auth_resend')}
            </button>
            <button
              onClick={() => { setStep('phone'); setOtp(''); setError(''); setResendTimer(0); }}
              style={{
                width: '100%', marginTop: '8px', padding: '12px',
                borderRadius: '12px', background: 'none', border: 'none',
                color: 'var(--text-muted)', fontSize: '13px',
                fontFamily: 'inherit', cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              {t('auth_changeNumber')}
            </button>
          </>
        )}
      </div>

      <Testimonials />

      {/* ── عن قادر ── */}
      <div style={{
        width: '100%', maxWidth: 380, marginTop: 28,
        background: 'linear-gradient(135deg,#1a1060,#2d1b8e)',
        borderRadius: 18, padding: '22px 20px',
        border: '1px solid rgba(255,215,0,0.15)',
      }}>
        <div style={{ borderRight: isRTL ? '3px solid #FFD700' : 'none', borderLeft: isRTL ? 'none' : '3px solid #FFD700', paddingRight: isRTL ? 12 : 0, paddingLeft: isRTL ? 0 : 12, marginBottom: 16 }}>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, lineHeight: 1.9, fontFamily: 'Tajawal,sans-serif', margin: 0 }}>
            {t('home_aboutDesc')}
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
          {[
            { icon: '🎓', key: 'home_feature1' },
            { icon: '🏆', key: 'home_feature2' },
            { icon: '📊', key: 'home_feature3' },
            { icon: '📋', key: 'home_feature4' },
          ].map(f => (
            <div key={f.key} style={{
              background: 'rgba(255,255,255,0.06)', borderRadius: 10,
              padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 7,
            }}>
              <span style={{ fontSize: 16 }}>{f.icon}</span>
              <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, fontFamily: 'Tajawal,sans-serif' }}>{t(f.key)}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <a href="mailto:support@qadder.sa" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, textDecoration: 'none', fontFamily: 'Tajawal,sans-serif' }}>
              📧 support@qadder.sa
            </a>
            <a href="https://t.me/qadder_sa" target="_blank" rel="noreferrer" style={{ color: '#229ED9', fontSize: 11, textDecoration: 'none', fontFamily: 'Tajawal,sans-serif', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="15" height="15"><circle cx="24" cy="24" r="24" fill="#229ED9"/><path fill="#fff" d="M35.2 14.3 30.9 34c-.3 1.4-1.2 1.8-2.4 1.1l-6.6-4.9-3.2 3.1c-.4.4-.6.6-1.2.6l.4-6.7 12.2-11c.5-.5-.1-.7-.8-.3L14.4 26.1l-6.6-2.1c-1.4-.4-1.5-1.4.3-2.1l25.9-10c1.2-.4 2.2.3 1.2 2.4z"/></svg>
              تيليقرام
            </a>
            <a href="https://x.com/qaddersa" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', fontSize: 11, textDecoration: 'none', fontFamily: 'Tajawal,sans-serif', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.63 5.905-5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              @qaddersa
            </a>
          </div>
          <a href="/about.html" target="_blank" rel="noreferrer" style={{ color: '#FFD700', fontSize: 11, fontWeight: 700, textDecoration: 'none', fontFamily: 'Tajawal,sans-serif' }}>
            {t('readMore')}
          </a>
        </div>
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '24px' }}>
        {t('auth_agree')}
      </p>
    </div>
  );
}
